import DateLogic from '../helpers/date-logic'

const identity = x => x

/**
 * A DateRange object can represent a single day or a range of days. It is
 * equipped to be readily modified in ways that the calendar application needs.
 */
export default class DateRange {
  static get today() {
    return new DateRange(DateLogic.today, DateLogic.today)
  }

  static get thisWeek() {
    return new DateRange(...DateLogic.getCurrentWeekStartEnd())
  }

  static fromArray([start, end]) {
    return new DateRange(start, end)
  }

  constructor(start, end = start) {
    this.range = [start, end]

    this.start = start
    this.end = end
  }

  get date() {
    if (this.isSingleDay()) {
      return this.start
    } else {
      throw new Error(
        'Tried to get the date from a DateRange that spans over multiple days.'
      )
    }
  }

  get length() {
    return Math.round(this.end.diff(this.start).as('days') + 1)
  }

  map(fn, ctx) {
    return DateLogic.getDaysInRange(this.start, this.end).map(fn, ctx)
  }

  offset(days) {
    return new DateRange(this.start.plus({ days }), this.end.plus({ days }))
  }

  equals(range) {
    return range && this.start.equals(range.start) && this.end.equals(range.end)
  }

  toWeekStart() {
    let [date] = DateLogic.getDaysInWeek(this.start)
    return new DateRange(date)
  }

  toWeek() {
    let [start, ...rest] = DateLogic.getDaysInWeek(this.start)
    let end = rest.pop()

    return new DateRange(start, end)
  }

  expand() {
    if (this.isRange()) {
      return this
    }

    return this.isCurrent ? DateRange.thisWeek : this.toWeek()
  }

  contract() {
    if (this.isSingleDay()) {
      return this
    }

    return this.isCurrent ? DateRange.today : this.toWeekStart()
  }

  isSingleDay() {
    return DateLogic.isSingleDay(this.range)
  }

  isRange() {
    return !this.isSingleDay()
  }

  get isCurrent() {
    if (this.isSingleDay()) {
      return this.isCurrentDay()
    } else {
      return this.isCurrentWeek()
    }
  }

  isCurrentDay() {
    return DateLogic.isCurrentDay(this.range)
  }

  isCurrentWeek() {
    return DateLogic.isCurrentWeek(this.range)
  }

  previous() {
    return this.offset(this.length * -1)
  }

  next() {
    return this.offset(this.length)
  }

  today() {
    if (this.isCurrent) {
      return this
    }

    return this.isSingleDay() ? DateRange.today : DateRange.thisWeek
  }

  toArray(fn = identity) {
    return this.map(fn)
  }

  toJSDateArray() {
    return this.toArray(date => date.toJSDate())
  }
}
